// Helpers for the opaque per-user `User.config` JSON blob: full-blob replace, no
// server-side merge, so setters read-modify-write the whole blob to keep unknown keys.

export const WEEK_START_CONFIG_KEY = 'schedulers:first-day';

// FullCalendar `firstDay`-compatible day-of-week (0 = Sunday … 6 = Saturday).
export type WeekStartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const DEFAULT_WEEK_START: WeekStartDay = 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isWeekStartDay = (value: number): value is WeekStartDay =>
  Number.isInteger(value) && value >= 0 && value <= 6;

// Returns `{}` for null/empty/malformed input or any non-object parse result.
export const parseUserConfig = (raw: string | null | undefined): Record<string, unknown> => {
  if (!raw) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const serializeUserConfig = (config: Record<string, unknown>): string =>
  JSON.stringify(config);

// Falls back to DEFAULT_WEEK_START when missing, malformed or out of range.
export const getWeekStart = (raw: string | null | undefined): WeekStartDay => {
  const stored = parseUserConfig(raw)[WEEK_START_CONFIG_KEY];
  const day = typeof stored === 'string' ? Number(stored) : stored;

  if (typeof day === 'number' && isWeekStartDay(day)) {
    return day;
  }

  return DEFAULT_WEEK_START;
};

// Read-modify-write: preserves every other key in the blob.
export const setWeekStart = (raw: string | null | undefined, day: WeekStartDay): string =>
  serializeUserConfig({ ...parseUserConfig(raw), [WEEK_START_CONFIG_KEY]: day });

// ----------------------------------------------------------------------
// Per-user resource-row order for the schedulers (SPAZ2-3205). Each calendar
// stores a single global ordered list of row ids (meeting rooms / guest rooms)
// under its own key; the order is applied client-side within each hotel group.
// ----------------------------------------------------------------------

export const ACTIVITIES_ROOM_ORDER_CONFIG_KEY = 'schedulers:activities:room-order';
export const GUEST_ROOMS_ROOM_ORDER_CONFIG_KEY = 'schedulers:guest-rooms:room-order';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

export const getRoomOrder = (raw: string | null | undefined, key: string): string[] => {
  const value = parseUserConfig(raw)[key];
  return isStringArray(value) ? value : [];
};

export const setRoomOrder = (
  raw: string | null | undefined,
  key: string,
  orderedRoomIds: string[]
): string => serializeUserConfig({ ...parseUserConfig(raw), [key]: orderedRoomIds });
